// Типы для данных отчета по результатам ПО (для отправки на бэкенд)
export type ResultPODataBackend = {
    examPlan: number;
    examinedTotal: number;
    recommendedDispensary: number;
    recommendedHospitalization: number;
    recommendedSurgery: number;
    recommendedFederalTreatment: number;
    recommendedTelemedWithFC: number;
    diagnosesTotal: number;
    firstTimeDiagnoses: number;
    annualInspectionPlan: number;
};

// Типы для данных отчета по результатам ПО детей (для отправки на бэкенд)
export type ResultPOChildrenDataBackend = {
    registeredDispensary: number;
    hospitalized: number;
    surgeryPerformed: number;
    referredToFederalCenter: number;
    telemedWithFCPerformed: number;
};

// Тип для отправки на бэкенд
export type ReportDataBackend = {
    reportingWeek: number;
    reportingYear: number;
    owner?: string | null;
    annualInspectionPlan?: number;
    resultPO: ResultPODataBackend;
    resultPOChildren: ResultPOChildrenDataBackend;
};

// Типы для значений полей отчета (для UI)
export type ReportFieldValue = {
    weekly: number | null; // Текущее значение (за неделю)
    total: number | null;  // Сумма за текущий год
    totalWithPrevYear?: number | null; // Сумма за текущий год + предыдущий год
};

// Типы для данных отчета по результатам ПО (для UI)
export type ResultPOData = {
    [key: string]: ReportFieldValue;
};

// Типы для данных отчета по результатам ПО детей (для UI)
export type ResultPOChildrenData = {
    [key: string]: ReportFieldValue;
};

// Общий тип для всех данных отчета (для UI)
export type ReportData = {
    annualInspectionPlan?:number | null
    ownerSectorName?:string;
    resultPO: ResultPOData;
    resultPOChildren: ResultPOChildrenData;
};

// Тип для изменения значения поля
export type FieldChangeParams = {
    section: 'resultPO' | 'resultPOChildren';
    key: string ;
    field: keyof ReportFieldValue;
    value: number | null;
};

// Вспомогательный тип для приведения строковых ключей
export type ReportDataSection = {
    [key: string]: {
        [key: string]: ReportFieldValue;
    };
};

// Функция для преобразования данных UI в формат для бэкенда
export const convertToBackendFormat = (data: ReportData, week: number, year: number): ReportDataBackend => {
    // Создаем пустые объекты для заполнения только измененными значениями
    const resultPO: Partial<ResultPODataBackend> = {};
    const resultPOChildren: Partial<ResultPOChildrenDataBackend> = {};

    // Заполняем только те данные, которые были введены
    Object.entries(data.resultPO).forEach(([key, value]) => {
        if (value.weekly !== null && value.weekly !== undefined) {
            (resultPO as any)[key] = value.weekly;
        }
    });

    Object.entries(data.resultPOChildren).forEach(([key, value]) => {
        if (value.weekly !== null && value.weekly !== undefined) {
            (resultPOChildren as any)[key] = value.weekly;
        }
    });

    // Проверяем, есть ли данные для отправки
    const hasResultPOData = Object.keys(resultPO).length > 0;
    const hasResultPOChildrenData = Object.keys(resultPOChildren).length > 0;

    // Создаем итоговый объект, включая только непустые секции
    const result: Partial<ReportDataBackend> = {
        reportingWeek: week,
        reportingYear: year
    };

    if (hasResultPOData) {
        result.resultPO = resultPO as ResultPODataBackend;
    }

    if (hasResultPOChildrenData) {
        result.resultPOChildren = resultPOChildren as ResultPOChildrenDataBackend;
    }
    if (data.annualInspectionPlan) {
        result.annualInspectionPlan = data.annualInspectionPlan as number;
    }
    return result as ReportDataBackend;
};

// Функция для преобразования данных с бэкенда в формат для UI
export const convertFromBackendFormat = (data: any): ReportData => {
    const resultPO: ResultPOData = {};
    const resultPOChildren: ResultPOChildrenData = {};
    // Преобразуем данные resultPO
    Object.entries(data.resultPO || {}).forEach(([key, value]) => {
        const valueArray = value as number[];
        resultPO[key] = {
            weekly: valueArray[0] !== undefined ? valueArray[0] : null,
            total: valueArray[1] !== undefined ? valueArray[1] : null
        };
    });

    // Преобразуем данные resultPOChildren
    Object.entries(data.resultPOChildren || {}).forEach(([key, value]) => {
        const valueArray = value as number[];
        resultPOChildren[key] = {
            weekly: valueArray[0] !== undefined ? valueArray[0] : null,
            total: valueArray[1] !== undefined ? valueArray[1] : null,
            totalWithPrevYear: valueArray[2] !== undefined ? valueArray[2] : null
        };
    });

    return {
        annualInspectionPlan: data.annualInspectionPlan,
        resultPO,
        resultPOChildren
    };
};
